
import { IBatchInfo } from 'components/common/DataGridDevEx/DataGrid.properties';
import { toObjectified } from 'api/baseApi';
import { APIMethod, Payload, prepareUrl, useDelete, useFetch, useFetchMutation, usePost, useUpdate } from 'api/reactQuery/queryService';

import { IFormInfo, IProductReceiptDetailsResponse, IParkedProductReceiptFacade, IParkedProductReceiptResponse, IProductReceiptFreightDetailsResponse, IProductReceiptFreightFacade, IProductReceiptContextPageFacade, IProductReceiptContextResponse, IProductReceiptGridLineFacade, IProductReceiptGridLineResponse, IUpdateProductReceiptLineDetailsResponse, IProductReceiptLineFacade, IProductReceiptLineResponse, IUpdateProductReceiptGridLineCriteriaFacade, IUpdateProductReceiptGridLineResponse, IDeleteRecordResponse, IOverChargeRFCFacade, IProcessProductReceiptResponse, IProcessProductReceiptConfirmationFacade, IUpdateProcessProductReceiptConfirmationCriteriaFacade, IProcessProductReceiptConfirmationResponse, IProcessProductReceiptCriteriaFacade, IOverchargeNoticeResponse, IOverchargeNoticeFacade, IMessageModelFacade } from 'api/swaggerTypes';
import { Inline, IObjectified, objectify } from 'api/utils';
import { APIRoutes } from './constants';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';

// Product Receipt Context Response Parser
const ProductReceiptContextResponseParser = (response: IProductReceiptContextResponse): IObjectifiedProductReceiptContextResponse => {
    const { ProductReceiptContext } = response;

    return {
        ...response,
        ProductReceiptContext: toObjectified(ProductReceiptContext as unknown as Inline<IProductReceiptContextPageFacade>)
    };
};

// Objectify Product Receipt Context
export type IObjectifiedProductReceiptContextResponse = Omit<IProductReceiptContextResponse, 'ProductReceiptContext'> & {
    ProductReceiptContext: IObjectified<IProductReceiptContextPageFacade>;
};

// Retrieve Product Receipt Context
export const useRetrieveProductReceiptContext = () =>
    useFetchMutation<Payload<{ ProductReceiptId: number }>, IObjectifiedProductReceiptContextResponse>(
        APIRoutes.productReceipts.context.action,
        null,
        {
            inline: true,
            responseParser: ProductReceiptContextResponseParser
        }
    );

const ProductReceiptLineDetailsBinResponseParser = (response): any => {
    const { Bin } = response;

    return {
        ...response,
        Bin: toObjectified(Bin ?? {})
    };
};

export const useRetrieveLineDetailsInitialBin = () =>
    useFetchMutation<Payload<{ Warehouse: string; ProductId: number }>, any>(
        APIRoutes.productReceipts.productReceipt.lineDetails.initialBin,
        null,
        {
            inline: true,
            responseParser: ProductReceiptLineDetailsBinResponseParser
        }
    );

// Calculate / Update Product Receipt Context
export const useUpdateProductReceiptContext = () =>
    useUpdate<Payload<{ ProductReceiptId: number } & { ProductReceiptContext: IProductReceiptContextPageFacade } & { urlQueryParams: { ChangedField: string } }>, IObjectifiedProductReceiptContextResponse>
        (APIRoutes.productReceipts.context.action, null, { inline: true, responseParser: ProductReceiptContextResponseParser });

// Apply Product Receipt Context
export const useApplyProductReceiptContext = () => {
    return usePost<Payload<{ ProductReceiptContext: IProductReceiptContextPageFacade; ProductReceiptId: number }>, IObjectifiedProductReceiptContextResponse>
        (APIRoutes.productReceipts.context.action, null, { inline: true, responseParser: ProductReceiptContextResponseParser });
};

const productReceiptDetailsResponseParser = (response) => {
    const ProductReceiptDetails = response.ProductReceiptDetails && toObjectified(response.ProductReceiptDetails as unknown as Inline<IObjectifiedProductReceiptsDetailsResponse>);

    return {
        ...response,
        ProductReceiptDetails: {
            inlineObject: ProductReceiptDetails?.inlineObject,
        }
    };

    return {
        ...response,
    };
};

export type IObjectifiedProductReceiptsDetailsResponse = IProductReceiptDetailsResponse & {
    'TaxClass'?: string;
    'Comment'?: string;
    'Status'?: boolean;
    'Forms'?: IFormInfo[];
};

export const useFetchProductReceiptsDetails = (ProductReceiptId: number) => {
    return useFetch<IObjectifiedProductReceiptsDetailsResponse>(
        {
            url: APIRoutes.productReceipts.productReceipt.details.action,
            urlParams: { ProductReceiptId },
            queryConfig: { enabled: !!ProductReceiptId },
            options: { inline: true, responseParser: productReceiptDetailsResponseParser }
        }
    );
};

export const useUpdateProductReceiptDetails = () => {
    return usePost<
        Payload<{
            ProductReceiptId: number;
            TaxClass: string;
            Comment?: string;
        }, IBatchInfo>,
        IObjectifiedProductReceiptsDetailsResponse
    >(
        APIRoutes.productReceipts.productReceipt.details.action,
        null,
        { responseParser: productReceiptDetailsResponseParser }
    );
};

const retrieveParkedReceiptResponseParser = (response) => {

    return {
        ...response,
        ParkedProductReceipt: response.ParkedProductReceipt ? response.ParkedProductReceipt.map((d) => objectify({ ...d, DateTime: `${d?.Entered.Value} ${d?.Time?.Display}` }, null, 'Inline')) : []
    };
};

export type IObjectifiedParkedProductReceiptResponse = Omit<IParkedProductReceiptResponse, 'ParkedProductReceipt'> & {
    ParkedProductReceipt: IObjectified<IParkedProductReceiptFacade>[];
};

export const useRetrieveParkedReceipts = () => {

    return usePost<Payload<{ Warehouse?: string; Username?: string; SupplierId?: string; ParkedReceiptsOnly?: boolean }, IBatchInfo>, IObjectifiedParkedProductReceiptResponse>
        (APIRoutes.productReceipts.parkedReciepts.retrieve, null, { inline: true, responseParser: retrieveParkedReceiptResponseParser });
};
export interface IObjectifiedFreightDetailsResponse {
    ProductReceiptFreightDetails: {
        inlineObject: IProductReceiptFreightFacade;
        schema: IProductReceiptFreightFacade;
    };
    Status?: boolean;
    Forms?: IFormInfo[];
}

const freightDetailsResponseParser = (response: IProductReceiptFreightDetailsResponse): IObjectifiedFreightDetailsResponse => {
    const ProductReceiptFreightDetails = response.ProductReceiptFreightDetails && toObjectified(response.ProductReceiptFreightDetails as unknown as Inline<IProductReceiptFreightFacade>);

    return {
        ...response,
        ProductReceiptFreightDetails: {
            inlineObject: ProductReceiptFreightDetails?.inlineObject,
            schema: response.ProductReceiptFreightDetails
        }
    };
};

export const useFetchFreightDetails = (ProductReceiptId: number) => {
    return useFetch<IObjectifiedFreightDetailsResponse>(
        {
            url: APIRoutes.productReceipts.FreightDetails.retrieve,
            urlParams: { ProductReceiptId },
            queryConfig: { enabled: !!ProductReceiptId },
            options: { inline: true, responseParser: freightDetailsResponseParser }
        }
    );
};

export type IUpdateFreightDetailsPayload = Payload<{ ProductReceiptId: string }>;

export const useUpdateFreightDetails = () =>
    usePost<IUpdateFreightDetailsPayload, IProductReceiptFreightDetailsResponse>(
        APIRoutes.productReceipts.FreightDetails.update,
        null,
        { inline: true }
    );

const productReceiptLinesResponseParser = (response): any => {
    const { ProductReceiptGridLine } = response;

    return {
        ...response,
        ProductReceiptGridLine: ProductReceiptGridLine.map((o) => objectify(o as Inline<IProductReceiptGridLineFacade>))
    };
};

export type IObjectifiedProductReceiptLinesResponse = Omit<IProductReceiptGridLineResponse, 'ProductReceiptGridLine'> & {
    ProductReceiptGridLine: IObjectified<IProductReceiptGridLineFacade>[];
};

export type IObjectifiedProductReceiptGridLineResponse = Omit<IProductReceiptGridLineResponse, 'ProductReceiptGridLine'> & {
    ProductReceiptGridLine: IObjectified<IProductReceiptGridLineFacade>;
};

export type IRetrieveProductReceiptLinesPayload = Payload<{ ProductReceiptId: number }, IBatchInfo>;

export const useRetrieveProductReceiptLines = () =>
    useFetchMutation<IRetrieveProductReceiptLinesPayload, IObjectifiedProductReceiptLinesResponse>
        (APIRoutes.productReceipts.productReceipt.lines.retrieve, null, { inline: true, responseParser: productReceiptLinesResponseParser });

export const useUpdateProductReceiptLine = () => {

    return usePost<Payload<{ ProductReceiptId: number; LineNumber: number } & IUpdateProductReceiptGridLineCriteriaFacade>, IUpdateProductReceiptGridLineResponse>
        (APIRoutes.productReceipts.productReceipt.lines.update, null, { inline: true });
};

const productReceiptGridLineResponseParser = (response) => {

    return {
        ...response,
        ProductReceiptGridLine: response.ProductReceiptGridLine && toObjectified(response.ProductReceiptGridLine as Inline<IProductReceiptGridLineFacade>)
    };
};

export const useCalculateProductReceiptLine = () => {

    return useUpdate<Payload<{ ProductReceiptId: number; LineNumber: number; ProductReceiptGridLine: IProductReceiptGridLineFacade; urlQueryParams: { ChangedField: string } } & IProductReceiptGridLineFacade>, IObjectifiedProductReceiptGridLineResponse>
        (APIRoutes.productReceipts.productReceipt.lines.calculate, null, { inline: true, responseParser: productReceiptGridLineResponseParser });
};

export const useDeleteProductReceiptLine = () => {

    return useDelete<Payload<{ ProductReceiptId: number; LineNumber: number }>, IDeleteRecordResponse>
        (APIRoutes.productReceipts.productReceipt.lines.delete, null, { inline: true });
};

// RETREIVE LINE DETAILS

const productReceiptsLineDetailsResponseParser = (response): IProductReceiptLineResponse => {
    const { ProductReceiptLine } = response;

    return {
        ...response,
        ProductReceiptLine: toObjectified(ProductReceiptLine as Inline<IProductReceiptLineFacade>)
    };
};

export const useFetchProductReceiptLineDetails = (ProductReceiptId: number, LineNumber: number) => {
    return useFetch<any>(
        {
            url: APIRoutes.productReceipts.productReceipt.lineDetails.retrieve,
            urlParams: { ProductReceiptId, LineNumber },
            queryConfig: { enabled: !!ProductReceiptId && LineNumber > 0 },
            options: { inline: true, responseParser: productReceiptsLineDetailsResponseParser }
        }
    );
};

// CALCULATE LINE DETAILS

export type IObjectifiedCalculateProductReceiptLineDetailsResponse = Omit<IUpdateProductReceiptLineDetailsResponse, 'ProductReceiptLine'> & {
    ProductReceiptLine?: IObjectified<IProductReceiptLineFacade>;
};

export type InlineProductReceiptLineDetailsResponse = Omit<IUpdateProductReceiptLineDetailsResponse, 'ProductReceiptLine'> & {
    ProductReceiptLine?: Inline<IProductReceiptLineFacade>;
};

export const useCalculateProductReceiptLineDetails = () => {
    return useUpdate<Payload<any & { ProductReceiptId: number; LineNumber: number } & { urlQueryParams: { ChangedField: string } }>, IObjectifiedCalculateProductReceiptLineDetailsResponse>(
        APIRoutes.productReceipts.productReceipt.lineDetails.calculate,
        null,
        { inline: true, responseParser: ProductReceiptLineDetailsResponseParser },
    );
};

export const useSetProduceReceiptsLineDetailsQueryData = () => {
    const queryClient = useQueryClient();

    return (ProductReceiptId, LineNumber, dataToUpdate: any) => {
        const finalUrl = prepareUrl(APIRoutes.productReceipts.productReceipt.lineDetails.retrieve, { ProductReceiptId, LineNumber }, null, true);
        queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
    };
};

// UPDATE LINE DETAILS

export type IObjectifiedProductReceiptLineDetailsResponse = Omit<IUpdateProductReceiptLineDetailsResponse, 'ProductReceiptGridLine'> & {
    ProductReceiptLine: IObjectified<IProductReceiptLineFacade>;
};

const ProductReceiptLineDetailsResponseParser = (response: IUpdateProductReceiptLineDetailsResponse): IObjectifiedProductReceiptLineDetailsResponse => {
    const { ProductReceiptLine } = response;

    return {
        ...response,
        ProductReceiptLine: toObjectified(ProductReceiptLine as Inline<IProductReceiptLineFacade>)
    };
};

export const useUpdateProductReceiptLineDetails = () => {
    return usePost<Payload<{ ProductReceiptLineDetails: any; BinLot?: any; ProductReceiptId: number; LineNumber: number }>, IObjectifiedProductReceiptLineDetailsResponse>
        (APIRoutes.productReceipts.productReceipt.lineDetails.update, null, { inline: true, responseParser: ProductReceiptLineDetailsResponseParser });
};

const productReceiptSummaryResponseParser = (response): any => {
    const { ProductReceiptSummary } = response;
    ProductReceiptSummary.Date.Value = moment(ProductReceiptSummary.Date.Value).format('DD/MM/YYYY');

    return {
        ...response,
        ProductReceiptSummary: toObjectified(ProductReceiptSummary as Inline<any>)
    };
};
export const useRetrieveProductReceiptSummary = (ProductReceipt: number) =>
    useFetch<any>(
        {
            url: APIRoutes.productReceipts.summary.retrieve,
            urlParams: { ProductReceipt },
            queryConfig: { enabled: !!ProductReceipt },
            options: { inline: true, responseParser: productReceiptSummaryResponseParser }
        }
    );

const productReceiptOverchargeNoticeResponseParser = (response): any => {

    if (response?.OverchargeNotice?.NoticeMessage?.Value) {
        response.OverchargeNotice.NoticeMessage.Value = response.OverchargeNotice.NoticeMessage.Value.split('\\n').join('\n');
    }

    return {
        ...response,
        OverchargeNotice: response.OverchargeNotice && toObjectified(response.OverchargeNotice as Inline<IOverchargeNoticeFacade>)
    };
};

export const useRetrieveProductReceiptOverchargeNotice = () =>
    useFetchMutation<Payload<{ ProductReceiptId: number }>, IOverchargeNoticeResponse>(
        APIRoutes.productReceipts.productReceipt.overChargeNotice.retrieve,
        null,
        {
            inline: true,
            responseParser: productReceiptOverchargeNoticeResponseParser
        }
    );

const processReceiptResponseParser = (response) => {

    return {
        ...response,
        ProcessProductReceiptConfirmation: response?.ProcessProductReceiptConfirmation && response?.ProcessProductReceiptConfirmation.length > 0 && response?.ProcessProductReceiptConfirmation.map((o) => objectify(o as Inline<IOverChargeRFCFacade>)),
        Messages: response?.Messages && response?.Messages.length > 0 && response?.Messages.map((o) => objectify(o as Inline<IMessageModelFacade>))
    };
};

export const useProcessReceipt = () => {

    return usePost<Payload<{ ProductReceiptId: number } & IProcessProductReceiptCriteriaFacade>, IProcessProductReceiptResponse>
        (APIRoutes.productReceipts.productReceipt.process, null, { inline: true, responseParser: processReceiptResponseParser });
};

// LOAD PURCHASE ORDERS
const loadPurchaseOrderResponseParser = (response): any => {
    return response;
};

export type ILoadPurchaseOrderPayload = Payload<{ ProductReceiptId: number; PurchaseOrder: string }, IBatchInfo>;

export const useLoadPurchaseOrder = () =>
    usePost<ILoadPurchaseOrderPayload, any>
        (APIRoutes.productReceipts.productReceipt.load.purchaseOrder, null, { inline: true, responseParser: loadPurchaseOrderResponseParser });

export const useCalculateFastLine = () =>
    useUpdate<Payload<{ ProductReceiptId: number } & any & { urlQueryParams: { ChangedField: string } }>, any>
        (APIRoutes.productReceipts.fastEntry.calculateFastLine, null, { inline: false });

export const useCreateFastLine = () =>
    usePost<any, any>(
        APIRoutes.productReceipts.fastEntry.createFastLine,
        null,
        { inline: false }
    );
// PARK RECEIPT CALL
const parkedReceiptResponseParser = (response) => {

    return {
        ...response,
    };
};

export interface IObjectifiedParkedReceiptResponse {
    'Status'?: boolean;
    'Forms'?: IFormInfo[];
}

export const useParkProductReceipt = () => {
    return usePost<Payload<{ Note: string; ProductReceiptId: number }>, IObjectifiedParkedReceiptResponse>
        (APIRoutes.productReceipts.productReceipt.park.action, null, { inline: true, responseParser: parkedReceiptResponseParser });
};

export const useCancelProductReceipt = () =>
    usePost<Payload<{ ProductReceiptId: number }>, any>(APIRoutes.productReceipts.productReceipt.cancel.delete);

const receiptConfirmationResponseParser = (response) => {

    return {
        ...response,
        ProcessProductReceiptConfirmation: response?.ProcessProductReceiptConfirmation && toObjectified(response?.ProcessProductReceiptConfirmation as Inline<IProcessProductReceiptConfirmationFacade>)
    };
};

export const useReceiptConfirmation = () => {

    return usePost<Payload<{ ProductReceiptId: number } & IUpdateProcessProductReceiptConfirmationCriteriaFacade>, IProcessProductReceiptConfirmationResponse>
        (APIRoutes.productReceipts.productReceipt.receiptConfirmation, null, { inline: true, responseParser: receiptConfirmationResponseParser });
};
